import dayjs from 'dayjs';
import { ExerciseTemplate } from 'exercises/types';
import {
  EquipmentFilter,
  getExerciseMatches,
  isBaseExerciseTemplate,
  isEquipmentFilter,
  isMuscleGroupFilter,
  MuscleGroupFilter,
  popularExerciseTemplateIds,
} from 'hevy-shared';
import { makeAutoObservable } from 'mobx';
import { stores } from 'state/stores';
import { t } from 'translations/t';

interface SectionHeaderCell {
  type: 'section';
  title: string;
}

interface SpacerCell {
  type: 'spacer';
}

interface NoExercisesMatchSearchCell {
  type: 'no-exercises-match-search';
  searchText: string;
}

type KeyedExerciseTemplate = { key: string } & ExerciseTemplate;

export const isKeyedExerciseTemplate = (exercise: unknown): exercise is KeyedExerciseTemplate => {
  return isBaseExerciseTemplate(exercise) && 'key' in exercise;
};

export type ExerciseDataCell =
  | SpacerCell
  | KeyedExerciseTemplate
  | SectionHeaderCell
  | NoExercisesMatchSearchCell;

export class ExerciseLibraryViewModel {
  searchText: string = '';
  equipmentFilter: EquipmentFilter = 'all_equipment';
  musleGroupFilter: MuscleGroupFilter = 'all_muscles';

  constructor() {
    makeAutoObservable(this);
  }

  get allExercisesFiltered() {
    return getExerciseMatches({
      exercises: stores.exerciseTemplates.exercises.map(e => ({
        ...e,
        localized_muscle_group_name: e.muscle_group,
        hundred_percent_bodyweight_exercise: undefined,
        goal: undefined,
        level: undefined,
        category: undefined,
        granular_equipments: undefined,
      })),
      muscleGroupFilter: this.musleGroupFilter,
      equipmentType: this.equipmentFilter,
      searchText: this.searchText,
      language: stores.userPreferences.language,
    }).map(e => ({ ...e.exercise, key: e.exercise.id }));
  }

  get popularExercisesAlphabeticalFiltered() {
    const popularExerciseTemplates = popularExerciseTemplateIds
      .map(id => stores.exerciseTemplates.getExercise(id))
      .filter((e): e is ExerciseTemplate => !!e);

    return getExerciseMatches({
      exercises: popularExerciseTemplates.map(e => ({
        ...e,
        localized_muscle_group_name: e.muscle_group,
        hundred_percent_bodyweight_exercise: undefined,
        goal: undefined,
        level: undefined,
        category: undefined,
        granular_equipments: undefined,
      })),
      muscleGroupFilter: this.musleGroupFilter,
      equipmentType: this.equipmentFilter,
      searchText: this.searchText,
      language: stores.userPreferences.language,
    })
      .map(e => ({ ...e.exercise, key: `popular-${e.exercise.id}` }))
      .sort((a, b) => {
        return a.title.localeCompare(b.title);
      });
  }

  get data(): ExerciseDataCell[] {
    if (this.searchText.length) {
      if (this.allExercisesFiltered.length === 0) {
        return [
          {
            type: 'no-exercises-match-search',
            searchText: this.searchText,
          },
        ];
      }

      const allSection: ExerciseDataCell[] = [
        { type: 'section', title: t('web.exerciseLibrary.searchResults') },
        ...this.allExercisesFiltered,
      ];

      return allSection;
    }

    const popularSection: ExerciseDataCell[] =
      dayjs(stores.account.last_workout_at).unix() === 0 &&
      this.popularExercisesAlphabeticalFiltered.length
        ? [
            { type: 'section', title: t('web.exerciseLibrary.popularExercises') },
            ...this.popularExercisesAlphabeticalFiltered,
            { type: 'spacer' },
          ]
        : [];

    const allSection: ExerciseDataCell[] = [
      { type: 'section', title: t('web.exerciseLibrary.allExercises') },
      ...this.allExercisesFiltered,
    ];

    return [...popularSection, ...allSection];
  }

  onSearchTextUpdate = (value: string) => {
    this.searchText = value;
  };

  onEquipmentFilterUpdate = (value: string) => {
    if (isEquipmentFilter(value)) {
      this.equipmentFilter = value;
    }
  };

  onMuscleGroupFilterUpdate = (value: string) => {
    if (isMuscleGroupFilter(value)) {
      this.musleGroupFilter = value;
    }
  };
}
